/**
 * NotAuthenticated Component
 * 
 * Displays when user is not authenticated (401).
 * Shows a clear "Sign In" path and help text.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { useCapabilities } from './CapabilityProvider';
import { clearCapabilityCache } from '../../services/capabilityService';
import { useState } from 'react';

export default function NotAuthenticated() {
  const navigate = useNavigate();
  const location = useLocation();
  const { error, me } = useCapabilities();
  const [reauthenticating, setReauthenticating] = useState(false);
  
  const handleSignIn = async () => {
    setReauthenticating(true);
    try {
      // Clear capability cache
      clearCapabilityCache();
      
      // Clear any auth storage
      localStorage.clear();
      sessionStorage.clear();
      
      // In dev mode, headers are auto-injected, so reload should work
      // Preserve returnTo for redirect after auth
      const returnTo = location.pathname + location.search;
      window.location.href = `/admin?returnTo=${encodeURIComponent(returnTo)}`;
    } catch (err) {
      console.error('Failed to sign in:', err);
      setReauthenticating(false);
    }
  };
  
  const handleSwitchAccount = async () => {
    await handleSignIn();
  };
  
  const urlParams = new URLSearchParams(window.location.search);
  const isDebug = urlParams.get('debug') === '1' || window.location.hostname.includes('.dev.') || window.location.hostname.includes('localhost');
  
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>Sign In Required</h1>
        <p style={styles.message}>
          You need to sign in to access the Admin Dashboard.
        </p>
        
        <div style={styles.actions}>
          <button
            onClick={handleSignIn}
            style={styles.buttonPrimary}
            disabled={reauthenticating}
            aria-label="Sign in"
          >
            {reauthenticating ? 'Signing in...' : 'Sign In'}
          </button>
          <button
            onClick={handleSwitchAccount}
            style={styles.buttonSecondary}
            disabled={reauthenticating}
            aria-label="Switch account"
          >
            Switch Account
          </button>
        </div>
        
        <p style={styles.help}>
          If you believe you should have access, please contact your administrator.
        </p>
        
        {/* Debug panel (dev only) */}
        {isDebug && (
          <div style={styles.debugPanel}>
            <h3 style={styles.debugTitle}>Debug Information</h3>
            <div style={styles.debugContent}>
              <div><strong>Status:</strong> Unauthenticated (401)</div>
              <div><strong>Error Code:</strong> {error?.code || error?.status || 'unknown'}</div>
              <div><strong>Error Message:</strong> {error?.message || 'No error message'}</div>
              <div><strong>Is HTML Response:</strong> {error?.isHTML ? 'true' : 'false'}</div>
              {error?.isHTML && (
                <div style={styles.debugWarning}>
                  ⚠️ Received HTML 401 response (likely nginx). Backend should return JSON 401.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: '2rem',
  },
  content: {
    maxWidth: '600px',
    textAlign: 'center',
    backgroundColor: '#fff',
    padding: '3rem',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '1rem',
  },
  message: {
    fontSize: '1.125rem',
    color: '#374151',
    marginBottom: '2rem',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    marginTop: '2rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  buttonPrimary: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#fff',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonSecondary: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#374151',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  help: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginTop: '1.5rem',
  },
  debugPanel: {
    marginTop: '2rem',
    padding: '1rem',
    backgroundColor: '#fef3c7',
    borderRadius: '0.375rem',
    border: '1px solid #fbbf24',
    fontSize: '0.875rem',
    textAlign: 'left',
  },
  debugTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '0.75rem',
    color: '#111827',
  },
  debugContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    color: '#374151',
  },
  debugWarning: {
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#fee2e2',
    borderRadius: '0.25rem',
    color: '#991b1b',
  },
};

